import React, { forwardRef, useCallback } from 'react';
import moment from 'moment';
import { Table } from 'reactstrap';
import imgfSortAsc from '../../assets/img/ico-asc.png';
import imgfSortDesc from '../../assets/img/ico-desc.png';
import imgfLoading from '../../assets/img/preloader-black.gif';
import oClasses from './CustomerIndex.module.scss';
import { useCustomerListContext } from './CustomerListContext';
import { can } from '../../utils/permissions';
import { tCustomerTemplate } from '../../i18n/customerTemplateImport';

const CustomerList = ({ setOpenModal }) => {
  const { aCustomers, bIsLoading, fnLastItemRef } = useCustomerListContext();

  return (
    <Table hover responsive striped>
      <CustomerListHeader />
      <tbody>
        {aCustomers.map((oCustomer, nIdx) => (
          <CustomerListItem
            key={oCustomer.cust_nid}
            ref={nIdx === aCustomers.length - 1 ? fnLastItemRef : null}
            oCustomer={oCustomer}
            onEdit={() => setOpenModal('customerEdit', oCustomer)}
            onViewExtract={() => setOpenModal('customerExtract', oCustomer)}
            onViewTelemetry={() => setOpenModal('customerTelemetry', oCustomer)}
            onViewTemplateImport={() => setOpenModal('customerTemplateImport', oCustomer)}
            onEditPlanEnd={() => setOpenModal('customerPlanEnd', oCustomer)}
            onDeactivate={() => setOpenModal('customerDisable', oCustomer)}
          />
        ))}
        {bIsLoading && (
          <tr>
            <td colSpan="8" className="centered-text">
              <img alt='Carregando' src={imgfLoading} /> Carregando...
            </td>
          </tr>
        )}
      </tbody>
    </Table>
  );
};

const CustomerListHeader = () => {
  return (
    <thead>
      <tr>
        <CustomerHeaderColumn cColumnName="cust_nid" cText="#" />
        <CustomerHeaderColumn cColumnName="cust_cname" cText="Nome" />
        <CustomerHeaderColumn cColumnName="cust_bisactive" cText="Ativo" />
        <CustomerHeaderColumn cColumnName="cust_ccnpj" cText="CPF/CNPJ" bSortable={false} />
        <CustomerHeaderColumn cColumnName="cust_cstate" cText="UF" />
        <CustomerHeaderColumn cColumnName="cust_ccity" cText="Cidade" />
        <CustomerHeaderColumn cColumnName="cust_dcreated" cText="Data de criação" />
        <CustomerHeaderColumn cColumnName="cust_nid" cText="Ação" bSortable={false} />
      </tr>
    </thead>
  );
};

const CustomerHeaderColumn = ({ cColumnName, cText, bSortable = true }) => {
  const { oOrderBy, updateOrderBy } = useCustomerListContext();
  const { cColumn, cType } = oOrderBy;

  const onClick = useCallback(() => {
    updateOrderBy(cColumnName);
  }, [cColumnName, updateOrderBy]);

  const bSortingByColumn = bSortable && cColumn === cColumnName;

  return (
    <th onClick={bSortable ? onClick : null} className={bSortable ? oClasses.headerSortable : undefined}>
      {cText}
      {bSortingByColumn && <img className={oClasses.sortArrow} src={cType === 'asc' ? imgfSortAsc : imgfSortDesc} alt={cType} />}
    </th>
  );
};

const CustomerListItem = forwardRef(({
  oCustomer = {},
  onEdit = () => {},
  onEditPlanEnd = () => {},
  onDeactivate = () => {},
  onViewExtract = () => {},
  onViewTelemetry = () => {},
  onViewTemplateImport = () => {},
}, ref) => {
  const bHasTemplateImport = oCustomer.bHasTemplateImport === true
    || oCustomer.bHasTemplateImport === 1
    || oCustomer.bHasTemplateImport === '1';

  return (
    <tr ref={ref}>
      <td>{oCustomer.cust_nid}</td>
      <td title={oCustomer.cust_cname}>
        <span className="container-config abbreviate-text">{oCustomer.cust_cname}</span>
      </td>
      <td>{oCustomer.cust_bisactive ? 'Sim' : 'Não'}</td>
      <td>{oCustomer.cust_ccnpj}</td>
      <td>{oCustomer.cust_cstate}</td>
      <td>{oCustomer.cust_ccity}</td>
      <td>{oCustomer.cust_dcreated ? moment.parseZone(oCustomer.cust_dcreated).format('DD/MM/YYYY H:mm') : ''}</td>
      <td className={oClasses.customerActions}>
        {can('view-customer') && (
          <div className={oClasses.customerActionGroup}>
            <a className="rounded-btn edit pointer" title="Telemetria / Uso" onClick={onViewTelemetry}>
              <i className="icon-eye-opened" />
            </a>
            <a
              className={`rounded-btn edit ${bHasTemplateImport ? 'pointer' : oClasses.templateImportDisabled}`}
              title={tCustomerTemplate(bHasTemplateImport ? 'customerTemplate.importAction' : 'customerTemplate.importDisabled')}
              onClick={bHasTemplateImport ? onViewTemplateImport : undefined}
              aria-disabled={!bHasTemplateImport}
            >
              <i className="icon-document" />
            </a>
          </div>
        )}
        {can('edit-customer') && (
          <div className={oClasses.customerActionGroup}>
            <a className="rounded-btn edit pointer" title="Alterar vencimento" onClick={onEditPlanEnd}>
              <i className="icon-lock" />
            </a>
            <a className="rounded-btn edit pointer" title="Extrato Financeiro" onClick={onViewExtract}>
              <i className="icon-document" />
            </a>
            <a className="rounded-btn edit pointer" title="Visualizar informações ou editar" onClick={onEdit}>
              <i className="icon-edit" />
            </a>
            {!!oCustomer.cust_bisactive && (
              <a className="rounded-btn edit pointer" title="Desativar" onClick={onDeactivate}>
                <i className="icon-close" />
              </a>
            )}
          </div>
        )}
      </td>
    </tr>
  );
});

export default CustomerList;
